// src/components/Login.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../App.css'; // Import the main CSS file

const Login = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/api/auth/login/', {
        username: formData.username,
        password: formData.password
      });

      const accessToken = response.data.access || response.data.accessToken;
      const refreshToken = response.data.refresh || response.data.refreshToken;
      
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

      onLogin(response.data);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const loginStyles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#0a0a0a',
      padding: '2rem 1rem',
      fontFamily: 'Inter, system-ui, sans-serif'
    },
    wrapper: {
      maxWidth: '400px',
      width: '100%',
      textAlign: 'center'
    },
    header: {
      marginBottom: '2.5rem'
    },
    title: {
      fontSize: '3rem',
      fontWeight: '900',
      color: '#fff',
      margin: '0 0 0.5rem 0',
      letterSpacing: '-0.05em'
    },
    titleAccent: {
      color: '#3b82f6'
    },
    subtitle: {
      color: '#9ca3af',
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      margin: 0
    },
    card: {
      backgroundColor: '#171717',
      padding: '2.5rem',
      borderRadius: '1.25rem',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
      border: '1px solid #262626'
    },
    formGroup: {
      marginBottom: '1.5rem',
      textAlign: 'left'
    },
    label: {
      display: 'block',
      fontSize: '0.75rem',
      fontWeight: '600',
      color: '#737373',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      marginBottom: '0.5rem'
    },
    input: {
      width: '100%',
      padding: '0.75rem 1rem',
      backgroundColor: '#262626',
      border: '1px solid #404040',
      borderRadius: '0.75rem',
      color: '#fff',
      fontSize: '1rem',
      outline: 'none',
      transition: 'all 0.2s ease',
      boxSizing: 'border-box'
    },
    errorBox: {
      backgroundColor: 'rgba(127, 29, 29, 0.2)',
      border: '1px solid rgba(127, 29, 29, 0.5)',
      borderRadius: '0.75rem',
      padding: '0.75rem',
      marginBottom: '1.5rem'
    },
    errorText: {
      color: '#f87171',
      fontSize: '0.875rem',
      textAlign: 'center',
      fontWeight: '500'
    },
    button: {
      width: '100%',
      padding: '0.875rem',
      backgroundColor: '#2563eb',
      color: '#fff',
      border: 'none',
      borderRadius: '0.75rem',
      fontSize: '1rem',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    footer: {
      marginTop: '2rem',
      color: '#525252',
      fontSize: '0.75rem'
    }
  };

  return (
    <div style={loginStyles.container}>
      <div style={loginStyles.wrapper}>
        <div style={loginStyles.header}>
          <h1 style={loginStyles.title}>
            Fince<span style={loginStyles.titleAccent}>Flow</span>
          </h1>
          <p style={loginStyles.subtitle}>
            Private Financial Intelligence
          </p>
        </div>

        <div style={loginStyles.card}>
          <form onSubmit={handleSubmit}>
            <div style={loginStyles.formGroup}>
              <label htmlFor="username" style={loginStyles.label}>
                Identity
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                style={loginStyles.input}
                placeholder="Username"
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#404040';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div style={loginStyles.formGroup}>
              <label htmlFor="password" style={loginStyles.label}>
                Access Key
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                style={loginStyles.input}
                placeholder="••••••••"
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#404040';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {error && (
              <div style={loginStyles.errorBox}>
                <div style={loginStyles.errorText}>{error}</div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={loginStyles.button}
              onMouseOver={(e) => e.target.style.backgroundColor = '#3b82f6'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#2563eb'}
            >
              {loading ? 'Verifying...' : 'Initialize Session'}
            </button>
          </form>
        </div>

        <div style={loginStyles.footer}>
          System Locked • Authorized Personnel Only • v2.16
        </div>
      </div>
    </div>
  );
};

export default Login;